<script setup lang="ts">
import {
  EntityBranchItem,
  EntityBranchSkeleton,
  type IBranchCard
} from '~/components/entity/branch'
import type { IResponseData } from '~/types'
import type { IOrganization } from '~/components/entity/organization/types'
import { FeaturePagination } from '~/components/feature/pagination'
import { useFetchLocale } from '~/composables/useFetchLocale'

interface IProps {
  organization: IOrganization
  hideOrganizationDetails: boolean
  hidePagination: boolean
  perPage?: number
}

const props = withDefaults(defineProps<IProps>(), {
  hideOrganizationDetails: false,
  hidePagination: false,
  perPage: 10
})

const emit = defineEmits<{
  loaded: [hasData: boolean]
}>()

const config = useRuntimeConfig()
const baseApiUrl = config.public.baseApiUrl

const page = ref(1)

const branchesParams = computed<Record<string, string | number>>(() => ({
  'filter[organization_id]': props.organization.id,
  'filter[organization_type]': props.organization.organization_type,
  page: page.value,
  per_page: props.perPage
}))

const query = computed(() =>
  new URLSearchParams({
    ...Object.fromEntries(
      Object.entries(branchesParams.value || {}).map(([k, v]) => [k, String(v)])
    )
  }).toString()
)

const url = computed(() => `${baseApiUrl}/branches?${query.value}`)

const {
  data: rawBranches,
  status,
  refresh
} = useFetchLocale<IResponseData<IBranchCard[]>>(url, {
  key: computed(() => `branches-${props.organization.id}-${page.value}`),
  watch: false,
  immediate: true
})

const branches = computed(() => rawBranches.value)

watchEffect(() => {
  if (status.value === 'success') {
    emit('loaded', !!branches.value?.data?.length)
  }
})

watch(url, () => {
  refresh()
})

const fetchBranches = (pageNumber: number) => {
  page.value = pageNumber
}
</script>

<template>
  <section v-if="status === 'success'" aria-labelledby="branches">
    <div v-if="branches?.data?.length" class="CardsList">
      <Heading id="branches" tag="h2" class="sr-only">{{ $t('common.branches') }}</Heading>
      <EntityBranchItem
        v-for="branch in branches.data"
        :key="branch.id"
        :item="branch"
        :hide-organization-details="props.hideOrganizationDetails"
      />
      <div
        v-if="!props.hidePagination && branches.meta.last_page !== 1"
        class="mt-7 flex justify-end"
      >
        <FeaturePagination :meta="branches.meta" :links="branches.links" @change="fetchBranches" />
      </div>
    </div>
  </section>

  <section v-else>
    <EntityBranchSkeleton
      :count="props.perPage"
      :hide-organization-details="props.hideOrganizationDetails"
    />
  </section>
</template>
